import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import data from 'utilities/data'
import { withApolloV4Provider } from 'utilities/apollo'
import useGpsrDialog from 'hooks/use-gpsr-dialog'

import ComplexForm from 'components/redux-containers/complex-form-container'
import CarFileQuickPriceValuation from 'components/molecules/carfile-quick-price-valuation'
import FlexibleDialog from 'components/molecules/flexible-dialog'
import GpsrDialogForm from 'components/molecules/gpsr-dialog-form'

import { VALUATION } from 'config/routes'
import Checkbox from 'components/atoms/checkbox'

const StyledCarFileQuickPriceValuation = styled(CarFileQuickPriceValuation)`
  margin-bottom: ${({ theme }) => theme.sizings.lvl3};
`

function CarDataForm({
  className,
  carFile,
  fieldsets,
  manual,
  valuation,
  hasStickyNav,
  hasChevronInNextPrevCloseButtons,
  carId,
  showGpsrForm,
  ...restProps
}) {
  const match = useRouteMatch()
  const { t } = useTranslation()
  const { injectCarFileDataInComplexFormFieldsets } = data
  const enrichedFieldsets = carFile
    ? injectCarFileDataInComplexFormFieldsets(fieldsets, carFile.auto_id)
    : fieldsets

  const [dontShowGPSRMessageAgain, setDontShowGPSRMessageAgain] = useState(
    carFile?.gpsrMessageOff || false,
  )

  const {
    isGpsrDialogOpen,
    formikRef,
    handleCloseGpsrDialog,
    handleGpsrFormSubmit,
  } = useGpsrDialog(carFile, carId, showGpsrForm)

  const handleFormSubmitWithCheckbox = () => {
    if (formikRef.current) {
      const formValues = formikRef.current.values
      const valuesWithCheckbox = {
        ...formValues,
        dontShowGPSRMessageAgain,
        carId: carId,
      }
      handleGpsrFormSubmit(valuesWithCheckbox, carId)
    }
  }

  return (
    <div className={className}>
      {
        // Displays the 'Quick Price Valuation' component only when:
        // - the user is in the first step of the tax valuation flow
        // - the car has a license plate (for unknown license plate, it's impossible to do the calculation)
        // - the dealer is based in the Netherlands (there are no valuation API's available for other countries)
        match.path === `${VALUATION}/2/basic` && (
          <StyledCarFileQuickPriceValuation />
        )
      }

      <ComplexForm
        data={carFile}
        fieldsets={enrichedFieldsets}
        enableReinitialize
        hasStickyNav={hasStickyNav}
        hasChevronInNextPrevCloseButtons={hasChevronInNextPrevCloseButtons}
        carFileDetails
        {...restProps}
      />

      <FlexibleDialog
        open={isGpsrDialogOpen}
        closeHandler={handleCloseGpsrDialog}
        title={carFile ? t('gpsrForm.addGpsrDetails') : ''}
        content={
          <GpsrDialogForm ref={formikRef} onSubmit={handleGpsrFormSubmit} />
        }
        cancel
        cancelText={carFile ? t('cancel') : ''}
        onCancel={handleCloseGpsrDialog}
        submitText={carFile ? t('save') : ''}
        onSubmit={handleFormSubmitWithCheckbox}
        scrollableContent
        additionalFooterContent={
          <Checkbox
            name="dontShowGPSRMessageAgain"
            onChange={setDontShowGPSRMessageAgain}
            title={t('gpsrForm.dontShowGPSRMessageAgain')}
            checked={dontShowGPSRMessageAgain}
          />
        }
      />
    </div>
  )
}

CarDataForm.propTypes = {
  className: PropTypes.object,
  carFile: PropTypes.object,
  fieldsets: PropTypes.arrayOf(PropTypes.object),
  manual: PropTypes.bool,
  valuation: PropTypes.bool,
  hasStickyNav: PropTypes.bool,
  hasChevronInNextPrevCloseButtons: PropTypes.bool,
  carId: PropTypes.string,
  showGpsrForm: PropTypes.bool,
}

CarDataForm.defaultProps = {
  className: null,
  carFile: null,
  fieldsets: [],
  manual: false,
  valuation: false,
  hasStickyNav: false,
  hasChevronInNextPrevCloseButtons: false,
  carId: null,
  showGpsrForm: false,
}

export default withApolloV4Provider(CarDataForm)
